import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  LinearProgress,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Button
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  TrendingUp,
  TrendingDown,
  Group,
  Schedule,
  CheckCircle,
  Warning,
  Person,
  Refresh
} from '@mui/icons-material';
import { leadFlowService } from '../../services/leadFlow';
import { format, isToday, isThisWeek, parseISO } from 'date-fns';

const LeadFlowDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await leadFlowService.getLeadFlowDashboard();
      setDashboardData(data);
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !dashboardData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        action={
          <Button color="inherit" size="small" onClick={fetchDashboardData}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (!dashboardData) return null;

  const { total_users, active_flow, paused_flow, users } = dashboardData;
  
  // Calculate percentages
  const activePercentage = total_users > 0 ? (active_flow / total_users) * 100 : 0;
  const pausedPercentage = total_users > 0 ? (paused_flow / total_users) * 100 : 0;

  // Categorize paused users by time
  const pausedUsers = users.filter(user => !user.lead_flow_status?.enabled);
  const recentlyPaused = pausedUsers.filter(user => {
    if (!user.lead_flow_status?.paused_at) return false;
    return isToday(parseISO(user.lead_flow_status.paused_at));
  });
  const weeklyPaused = pausedUsers.filter(user => {
    if (!user.lead_flow_status?.paused_at) return false;
    return isThisWeek(parseISO(user.lead_flow_status.paused_at));
  });

  // Get recently paused users for quick overview
  const getRecentPausedUsers = () => {
    return pausedUsers
      .sort((a, b) => {
        const dateA = a.lead_flow_status?.paused_at ? new Date(a.lead_flow_status.paused_at) : new Date(0);
        const dateB = b.lead_flow_status?.paused_at ? new Date(b.lead_flow_status.paused_at) : new Date(0);
        return dateB - dateA;
      })
      .slice(0, 5);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUp color="primary" />
          Lead Flow Dashboard
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchDashboardData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Main Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold' }}>
                    {total_users}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Total Team Members
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <Group />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" color="success.main" sx={{ fontWeight: 'bold' }}>
                    {active_flow}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Active Lead Flow
                  </Typography>
                  <Typography variant="caption" color="success.main">
                    {activePercentage.toFixed(1)}% of total
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                  <PlayArrow />
                </Avatar>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={activePercentage} 
                color="success"
                sx={{ mt: 2 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h3" color="warning.main" sx={{ fontWeight: 'bold' }}>
                    {paused_flow}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Paused Lead Flow
                  </Typography>
                  <Typography variant="caption" color="warning.main">
                    {pausedPercentage.toFixed(1)}% of total
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                  <Pause />
                </Avatar>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={pausedPercentage} 
                color="warning"
                sx={{ mt: 2 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Analytics */}
      <Grid container spacing={3}>
        {/* Recent Activity */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Schedule color="primary" />
                Recent Pause Activity
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.50' }}>
                    <Typography variant="h4" color="error.main">
                      {recentlyPaused.length}
                    </Typography>
                    <Typography variant="caption">Paused Today</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.50' }}>
                    <Typography variant="h4" color="info.main">
                      {weeklyPaused.length}
                    </Typography>
                    <Typography variant="caption">Paused This Week</Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Recently Paused Users
              </Typography>
              
              {getRecentPausedUsers().length > 0 ? (
                <List dense>
                  {getRecentPausedUsers().map((user) => (
                    <ListItem key={user.id} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {user.full_name?.[0]}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={user.full_name}
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Paused by: {user.lead_flow_status?.paused_by || 'Unknown'}
                            </Typography>
                            {user.lead_flow_status?.paused_at && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {format(new Date(user.lead_flow_status.paused_at), 'MMM dd, HH:mm')}
                              </Typography>
                            )}
                            {user.lead_flow_status?.pause_reason && (
                              <Typography variant="caption" display="block" color="warning.main">
                                {user.lead_flow_status.pause_reason}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No recent pauses
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Team Overview */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Group color="primary" />
                Team Flow Status
              </Typography>
              
              {/* Flow Status Distribution */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2">Active Users</Typography>
                  <Typography variant="body2" color="success.main">
                    {active_flow}/{total_users}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={activePercentage} 
                  color="success"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2">Paused Users</Typography>
                  <Typography variant="body2" color="warning.main">
                    {paused_flow}/{total_users}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={pausedPercentage} 
                  color="warning"
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Role Distribution */}
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Team Composition
              </Typography>
              
              <Grid container spacing={1}>
                {['bda', 'team_lead'].map(role => {
                  const roleUsers = users.filter(user => user.role === role);
                  const activeRoleUsers = roleUsers.filter(user => user.lead_flow_status?.enabled);
                  
                  return (
                    <Grid item xs={6} key={role}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h5" color="primary">
                          {activeRoleUsers.length}/{roleUsers.length}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {role.replace('_', ' ').toUpperCase()}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={`${roleUsers.length > 0 ? Math.round((activeRoleUsers.length / roleUsers.length) * 100) : 0}% Active`}
                            size="small"
                            color={activeRoleUsers.length === roleUsers.length ? 'success' : 'default'}
                          />
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Current Issues Alert */}
      {paused_flow > 0 && (
        <Box sx={{ mt: 3 }}>
          <Alert 
            severity="warning" 
            sx={{ '& .MuiAlert-message': { width: '100%' } }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {paused_flow} team member{paused_flow > 1 ? 's have' : ' has'} paused lead flow
                </Typography>
                <Typography variant="caption">
                  This may affect lead distribution and response times
                </Typography>
              </Box>
              <Warning />
            </Box>
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default LeadFlowDashboard;